// src/features/orderdetails/OrderDetails.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Divider,
  IconButton,
  TableContainer,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import OrderStatusTimeline from "../orderTimeStamp";
import { OrderItem } from "../../app/stores/orderDetailStore";

const tableColumns = [
  "Product Name",
  "SKU",
  "Unit",
  "Ordered Qty",
  "Updated Qty",
  "Returned",
  "Actual Qty",
  "Price",
  "Tax",
  "After Tax Amount",
];

const OrderDetails: React.FC = observer(() => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { orderDetailStore } = useStore();

  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [selectedReturns, setSelectedReturns] = useState<any[]>([]);

  useEffect(() => {
    if (orderId) {
      orderDetailStore.loadOrder(Number(orderId));
    }
  }, [orderDetailStore, orderId]);

  const order = orderDetailStore.order;
  if (!order)
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );

  const createdDate = new Date(order.created_at).toLocaleDateString();
  const createdTime = new Date(order.created_at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Returned quantity map
  const returnedMap: { [productId: number]: number } = {};
  if (order.salesReturns?.history?.items) {
    order.salesReturns.history.items.forEach((srItem: any) => {
      const qty = Number(srItem.quantity) || 0;
      returnedMap[srItem.product_id] =
        (returnedMap[srItem.product_id] || 0) + qty;
    });
  }

  const handleOpenReturnDialog = (productId: number) => {
    if (!order?.salesReturns?.history?.items) return;
    const productReturns = order.salesReturns.history.items.filter(
      (sr: any) => sr.product_id === productId
    );
    setSelectedReturns(productReturns);
    setOpenReturnDialog(true);
  };

  return (
    <Box display="flex" gap={3} p={3}>
      {/* Left Side */}
      <Box flex={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600} ml={1}>
            Order #{order.unique_id}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" mb={2}>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary">
              Order Date & Time
            </Typography>
            <Typography>
              {createdDate} {createdTime}
            </Typography>
          </Box>
          <Box flex={1}>
            <Typography variant="caption" color="text.secondary">
              Ordered by
            </Typography>
            <Typography>{order.user.name}</Typography>
          </Box>
          {/* <Box flex={1}>
            <Typography variant="caption" color="text.secondary">
              Order Amount
            </Typography>
            <Typography>₹ {order.total_amount}</Typography>
          </Box> */}
        </Box>

        {/* Product Details Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>
              Product Details
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableColumns.map((col) => (
                      <TableCell key={col}>
                        <strong>{col}</strong>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.items.map((item: OrderItem, index: number) => {
                    const uq = item.updatedQuantity ?? [];
                    const updatedQuantity =
                      uq.length > 0 ? Number(uq[0].quantity) : 0;
                    const returnedQuantity = returnedMap[item.product_id] || 0;
                    const actualQty =
                      updatedQuantity > 0
                        ? updatedQuantity - returnedQuantity
                        : item.quantity - returnedQuantity;

                    const total = parseFloat(item.price) * actualQty;
                    const taxRate = Number(item.product.tax) || 0;
                    const taxAmount = total * (taxRate / 100);
                    const afterTaxAmount = total + taxAmount;

                    return (
                      <TableRow key={index}>
                        <TableCell>{item.product.name}</TableCell>
                        <TableCell>{item.product.sku}</TableCell>
                        <TableCell>{item.product.base_unit}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{updatedQuantity > 0 ? updatedQuantity : 0}</TableCell>
                        <TableCell>
                          {returnedQuantity > 0 ? (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() =>
                                handleOpenReturnDialog(item.product_id)
                              }
                            >
                              {returnedQuantity}
                            </Button>
                          ) : (
                            returnedQuantity
                          )}
                        </TableCell>
                        <TableCell>{actualQty}</TableCell>
                        <TableCell>₹{item.price}</TableCell>
                        <TableCell>{taxRate > 0 ? `${taxRate}% (₹${taxAmount.toFixed(2)})` : "₹0.00"}</TableCell>
                        <TableCell>₹{afterTaxAmount.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Right Side */}
      <Box flex={1}>
        <OrderStatusTimeline statusHistory={order.statusHistory} />

        {/* Bill Details */}
        <Box mt={3}>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Bill Details
          </Typography>
          {(() => {
            let subtotal = 0;
            let totalTax = 0;

            order.items.forEach((item: any) => {
              const uq = item.updatedQuantity ?? [];
              const updatedQuantity = uq.length > 0 ? Number(uq[0].quantity) : 0;
              const returnedQuantity = returnedMap[item.product_id] || 0;
              const actualQty =
                updatedQuantity > 0
                  ? updatedQuantity - returnedQuantity
                  : item.quantity - returnedQuantity;

              const total = parseFloat(item.price) * actualQty;
              const taxRate = Number(item.product.tax) || 0;
              const taxAmount = total * (taxRate / 100);

              subtotal += total;
              totalTax += taxAmount;
            });

            const grandTotal = subtotal + totalTax;

            return (
              <>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Total</Typography>
                  <Typography>₹{subtotal.toFixed(2)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Tax Amount</Typography>
                  <Typography>₹{totalTax.toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" mt={1}>
                  <Typography fontWeight={600}>Payable Amount</Typography>
                  <Typography fontWeight={600}>₹{grandTotal.toFixed(2)}</Typography>
                </Box>
              </>
            );
          })()}
        </Paper>
      </Box>
      </Box>

      {/* Sales Return Dialog */}
      <Dialog
        open={openReturnDialog}
        onClose={() => setOpenReturnDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Sales Return Details
          <Button
            onClick={() => setOpenReturnDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReturns.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Product</strong>
                    </TableCell>
                    <TableCell>
                      <strong>SKU</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Base Price</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Returned By</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Reason</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Quantity</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Amount</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedReturns.map((sr, idx) => {
                    const basePrice = parseFloat(sr.product?.base_price) || 0;
                    const qty = Number(sr.quantity) || 0;
                    const amount = basePrice * qty;
                    return (
                      <TableRow key={idx}>
                        <TableCell>{sr.product?.name || "-"}</TableCell>
                        <TableCell>{sr.product?.sku || "-"}</TableCell>
                        <TableCell>₹{basePrice.toFixed(2)}</TableCell>
                        <TableCell>{sr.user?.name || "-"}</TableCell>
                        <TableCell>{sr.reason?.name || "-"}</TableCell>
                        <TableCell>{qty}</TableCell>
                        <TableCell>₹{amount.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Sub Total: ₹
                  {selectedReturns
                    .reduce((acc, sr) => {
                      const basePrice =
                        parseFloat(sr.product?.base_price) || 0;
                      const qty = Number(sr.quantity) || 0;
                      return acc + basePrice * qty;
                    }, 0)
                    .toFixed(2)}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography>No return records found</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReturnDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default OrderDetails;
